import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ShieldAlert,
  AlertTriangle,
  Eye,
  MoreHorizontal,
  MapPin,
  Video,
  PhoneCall,
  Clock,
  CheckCircle2,
  Bell,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  FioriPageHeader,
  FioriFilterBar,
  FioriTable,
  FioriStatusBadge,
} from "@/components/fiori";
import type { FioriColumn } from "@/components/fiori";

// Mock data for alerts
const initialAlerts = [
  { id: "ALT-2025-089", typeKey: "breachAttempt", severityKey: "critical", location: "Riyadh Main DC - Zone C", timestampKey: "justNow", statusKey: "new", descriptionKey: "desc089" },
  { id: "ALT-2025-088", typeKey: "doorForced", severityKey: "high", location: "Jeddah DR - Gate 1", timestampKey: "15minsAgo", statusKey: "viewed", descriptionKey: "desc088" },
  { id: "ALT-2025-087", typeKey: "loitering", severityKey: "medium", location: "Riyadh Main DC - Lobby", timestampKey: "1hourAgo", statusKey: "actionTaken", descriptionKey: "desc087" },
  { id: "ALT-2025-086", typeKey: "deviceOffline", severityKey: "low", location: "Dammam Edge - Cam 04", timestampKey: "2hoursAgo", statusKey: "actionTaken", descriptionKey: "desc086" },
  { id: "ALT-2025-085", typeKey: "fireAlarm", severityKey: "critical", location: "Riyadh Main DC - Zone B", timestampKey: "yesterday", statusKey: "resolved", descriptionKey: "desc085" },
];

export default function SecurityAlerts() {
  const { t } = useTranslation();
  const alertTypeLabels = useMemo(() => ({
    breachAttempt: t('security.alertTypes.breachAttempt', 'Breach Attempt'),
    doorForced: t('security.alertTypes.doorForced', 'Door Forced'),
    loitering: t('security.alertTypes.loitering', 'Loitering'),
    deviceOffline: t('security.alertTypes.deviceOffline', 'Device Offline'),
    fireAlarm: t('security.alertTypes.fireAlarm', 'Fire Alarm'),
  }), [t]);

  const alertSeverityLabels = useMemo(() => ({
    critical: t('security.severityLevels.critical', 'Critical'),
    high: t('security.severityLevels.high', 'High'),
    medium: t('security.severityLevels.medium', 'Medium'),
    low: t('security.severityLevels.low', 'Low'),
  }), [t]);

  const alertStatusLabels = useMemo(() => ({
    new: t('security.alertStatuses.new', 'New'),
    viewed: t('security.alertStatuses.viewed', 'Viewed'),
    actionTaken: t('security.alertStatuses.actionTaken', 'Action Taken'),
    resolved: t('security.alertStatuses.resolved', 'Resolved'),
  }), [t]);

  const alertTimestampLabels = useMemo(() => ({
    justNow: t('security.time.justNow', 'Just now'),
    '15minsAgo': t('security.time.15minsAgo', '15 mins ago'),
    '1hourAgo': t('security.time.1hourAgo', '1 hour ago'),
    '2hoursAgo': t('security.time.2hoursAgo', '2 hours ago'),
    yesterday: t('security.time.yesterday', 'Yesterday'),
  }), [t]);

  const alertDescriptions = useMemo(() => ({
    desc089: t('security.alertDesc.desc089', 'Unauthorized access attempt detected at Server Hall 1. Biometric mismatch repeated 3 times.'),
    desc088: t('security.alertDesc.desc088', 'Perimeter gate sensor indicates forced entry. CCTV shows vehicle impact.'),
    desc087: t('security.alertDesc.desc087', 'Individual loitering near security desk for >20 minutes without badge.'),
    desc086: t('security.alertDesc.desc086', 'Camera feed signal lost. Maintenance ticket #4421 created.'),
    desc085: t('security.alertDesc.desc085', 'False alarm triggered by maintenance dust. System reset.'),
  }), [t]);

  const enrichedAlerts = useMemo(() => initialAlerts.map(a => ({
    ...a,
    type: alertTypeLabels[a.typeKey as keyof typeof alertTypeLabels] || a.typeKey,
    severity: alertSeverityLabels[a.severityKey as keyof typeof alertSeverityLabels] || a.severityKey,
    status: alertStatusLabels[a.statusKey as keyof typeof alertStatusLabels] || a.statusKey,
    timestamp: alertTimestampLabels[a.timestampKey as keyof typeof alertTimestampLabels] || a.timestampKey,
    description: alertDescriptions[a.descriptionKey as keyof typeof alertDescriptions] || a.descriptionKey,
  })), [alertTypeLabels, alertSeverityLabels, alertStatusLabels, alertTimestampLabels, alertDescriptions]);

  const [alerts, setAlerts] = useState(enrichedAlerts);
  const [selectedAlert, setSelectedAlert] = useState<any>(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredAlerts = useMemo(() => {
    let result = alerts;
    if (activeTab === "new") result = result.filter((a) => a.statusKey === "new");
    else if (activeTab === "critical") result = result.filter((a) => a.severityKey === "critical");
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter((a) => a.id.toLowerCase().includes(q) || a.type.toLowerCase().includes(q) || a.location.toLowerCase().includes(q));
    }
    return result;
  }, [alerts, activeTab, searchQuery]);

  const stats = useMemo(() => ({
    critical: alerts.filter((a) => a.severityKey === "critical" && a.statusKey !== "resolved").length,
    newAlerts: alerts.filter((a) => a.statusKey === "new").length,
    investigating: alerts.filter((a) => a.statusKey === "viewed").length,
    resolved: 14,
  }), [alerts]);

  const handleView = (alert: any) => {
    setSelectedAlert(alert);
    setViewOpen(true);
    if (alert.statusKey === "new") {
      setAlerts(alerts.map((a) => (a.id === alert.id ? { ...a, statusKey: "viewed", status: alertStatusLabels.viewed } : a)));
    }
  };

  const handleAction = (action: string) => {
    if (!selectedAlert) return;
    setAlerts(alerts.map((a) => (a.id === selectedAlert.id ? { ...a, statusKey: "actionTaken", status: alertStatusLabels.actionTaken } : a)));
    setViewOpen(false);
    toast.success(t('security.actionRecorded', 'Action Recorded') + ': ' + action, { description: t('security.alertStatusUpdated', 'Alert {{id}} status updated.').replace('{{id}}', selectedAlert.id) });
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case alertSeverityLabels.critical:
      case "Critical": return "bg-[#FFE5E5] text-[#DC2626] border-[#DC2626]";
      case alertSeverityLabels.high:
      case "High": return "bg-[#FEF3C7] text-[#D97706] border-[#D97706]";
      case alertSeverityLabels.medium:
      case "Medium": return "bg-[#FFF4E5] text-[#D97706] border-[#D97706]";
      default: return "bg-[#E8DCF5] text-[#5B2C93] border-[#5B2C93]";
    }
  };

  const activeFilterChips = useMemo(() => {
    const chips: { key: string; label: string; onRemove: () => void }[] = [];
    if (activeTab !== "all") chips.push({ key: "tab", label: `${t('common.filter', 'Filter')}: ${activeTab}`, onRemove: () => setActiveTab("all") });
    return chips;
  }, [activeTab]);

  const columns: FioriColumn<any>[] = useMemo(() => [
    {
      key: "id",
      header: t('security.alertId'),
      width: "140px",
      render: (a: any) => <span className="font-mono text-sm font-medium text-[#5B2C93]">{a.id}</span>,
    },
    {
      key: "type",
      header: t('common.type'),
      render: (a: any) => <span className="font-medium text-[#2C2C2C]">{a.type}</span>,
    },
    {
      key: "severity",
      header: t('security.severity'),
      render: (a: any) => (
        <Badge variant="outline" className={`${getSeverityColor(a.severity)} text-xs`}>
          {a.severity}
        </Badge>
      ),
    },
    {
      key: "location",
      header: t('common.location', 'Location'),
      render: (a: any) => (
        <div className="flex items-center gap-1.5 text-sm text-[#6B6B6B]">
          <MapPin className="h-3 w-3" />
          {a.location}
        </div>
      ),
    },
    {
      key: "timestamp",
      header: t('common.time'),
      render: (a: any) => <span className="text-sm text-[#6B6B6B]">{a.timestamp}</span>,
    },
    {
      key: "status",
      header: t('common.status'),
      render: (a: any) => (
        <FioriStatusBadge
          status={a.statusKey === "new" ? "pending" : a.statusKey === "viewed" ? "info" : a.statusKey === "actionTaken" ? "success" : a.statusKey === "resolved" ? "inactive" : "pending"}
          label={a.status}
        />
      ),
    },
    {
      key: "actions",
      header: t('common.actions'),
      align: "right" as const,
      width: "80px",
      render: (a: any) => (
        <Button variant="ghost" size="sm" className="h-8 gap-1 text-[#5B2C93]" onClick={(e) => { e.stopPropagation(); handleView(a); }}>
          <Eye className="h-4 w-4" /> {t('common.view', 'View')}
        </Button>
      ),
    },
  ], [alerts]);

  return (
    <div className="-mx-4 md:-mx-6 -mb-4 md:-mb-6 min-h-[calc(100vh-4rem)] bg-white px-4 md:px-6">
      {/* SAP Fiori Page Header */}
      <FioriPageHeader
        title={t('security.alerts.title', 'Security Alerts')}
        subtitle={t('security.alerts.subtitle', 'Real-time incident monitoring and response log')}
        icon={<ShieldAlert className="h-5 w-5 text-[#DC2626]" />}
        count={filteredAlerts.length}
      />

      {/* KPI Strip */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        {[
          { label: t('security.kpi.activeCritical', 'Active Critical'), value: stats.critical, icon: AlertTriangle, color: "#DC2626", bg: "#FFE5E5" },
          { label: t('security.kpi.newAlerts', 'New Alerts'), value: stats.newAlerts, icon: Bell, color: "#5B2C93", bg: "#E8DCF5" },
          { label: t('security.kpi.underInvestigation', 'Under Investigation'), value: stats.investigating, icon: Eye, color: "#D97706", bg: "#FEF3C7" },
          { label: t('security.kpi.resolvedToday', 'Resolved Today'), value: stats.resolved, icon: CheckCircle2, color: "#059669", bg: "#D1FAE5" },
        ].map((kpi) => (
          <div key={kpi.label} className="bg-white border border-[#E0E0E0] rounded-lg px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: kpi.bg }}>
                <kpi.icon className="h-4 w-4" style={{ color: kpi.color }} />
              </div>
              <div>
                <p className="text-xs text-[#6B6B6B] font-medium">{kpi.label}</p>
                <p className="text-xl font-semibold text-[#2C2C2C]">{kpi.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* SAP Fiori Filter Bar */}
      <div className="mb-6">
        <FioriFilterBar
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder={t('security.searchPlaceholder', 'Search alerts by ID, type, or location...')}
          activeFilters={activeFilterChips}
          onClearAll={() => setActiveTab("all")}
          filters={
            <div className="flex gap-1">
              {[
                { key: "all", label: t('security.filter.allAlerts', 'All Alerts') },
                { key: "new", label: t('security.filter.new', 'New'), dot: stats.newAlerts > 0 },
                { key: "critical", label: t('security.filter.criticalOnly', 'Critical Only') },
              ].map((tab) => (
                <Button
                  key={tab.key}
                  variant={activeTab === tab.key ? "default" : "ghost"}
                  size="sm"
                  className={`h-8 text-xs relative ${activeTab === tab.key ? "bg-[#5B2C93] text-white hover:bg-[#3D1C5E]" : "text-[#6B6B6B]"}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                  {tab.dot && activeTab !== tab.key && (
                    <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-[#DC2626]" />
                  )}
                </Button>
              ))}
            </div>
          }
        />
      </div>

      {/* SAP Fiori Table */}
      <FioriTable
        columns={columns}
        data={filteredAlerts}
        isLoading={false}
        rowKey={(a: any) => a.id}
        onRowClick={(a: any) => handleView(a)}
        rowClassName={(a: any) => a.status === "New" ? "bg-[#E8DCF5]/30" : ""}
        emptyIcon={<ShieldAlert className="h-10 w-10" />}
        emptyTitle={t('security.noAlerts')}
        emptyDescription={t('security.emptyDescription', 'All clear. No security alerts match your criteria.')}
        footerInfo={t('security.showingAlerts', 'Showing {{count}} of {{total}} alerts').replace('{{count}}', String(filteredAlerts.length)).replace('{{total}}', String(alerts.length))}
      />

      {/* Alert Details Dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-2xl p-0">
          <div className="px-6 pt-5 pb-4 border-b border-[#E0E0E0]">
            <DialogTitle className="flex items-center gap-2 text-xl">
              <AlertTriangle className="h-5 w-5 text-[#DC2626]" />
              {t('security.incidentDetails', 'Incident Details')}
            </DialogTitle>
            <DialogDescription className="mt-1">
              {selectedAlert?.id} \u2014 {selectedAlert?.timestamp}
            </DialogDescription>
          </div>

          {selectedAlert && (
            <div className="px-6 py-4 space-y-5">
              <div className="flex flex-wrap items-center justify-between gap-2 p-4 bg-[#FAFAFA] rounded-lg border border-[#E0E0E0]">
                <div className="space-y-1">
                  <span className="text-xs text-[#6B6B6B] uppercase tracking-wider">{t('security.incidentType', 'Incident Type')}</span>
                  <p className="font-semibold text-lg text-[#2C2C2C]">{selectedAlert.type}</p>
                </div>
                <Badge className={`${getSeverityColor(selectedAlert.severity)} text-sm px-3 py-1 shrink-0`}>
                  {selectedAlert.severity} {t('security.severityLevels.label', 'Severity')}
                </Badge>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <span className="text-xs text-[#6B6B6B] flex items-center gap-1 uppercase tracking-wider">
                    <MapPin className="h-3 w-3" /> {t('common.location', 'Location')}
                  </span>
                  <p className="font-medium text-[#2C2C2C]">{selectedAlert.location}</p>
                </div>
                <div className="space-y-1">
                  <span className="text-xs text-[#6B6B6B] flex items-center gap-1 uppercase tracking-wider">
                    <Clock className="h-3 w-3" /> {t('security.detectedAt', 'Detected At')}
                  </span>
                  <p className="font-medium text-[#2C2C2C]">{selectedAlert.timestamp}</p>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="text-xs text-[#6B6B6B] uppercase tracking-wider">{t('common.description', 'Description')}</h4>
                <p className="text-sm text-[#2C2C2C] bg-[#FAFAFA] p-3 rounded-md border border-[#E0E0E0]">
                  {selectedAlert.description}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button variant="outline" className="flex-1 gap-2 border-[#E0E0E0]">
                  <Video className="h-4 w-4" /> {t('security.viewPlayback', 'View Playback')}
                </Button>
                <Button variant="outline" className="flex-1 gap-2 border-[#E0E0E0]">
                  <PhoneCall className="h-4 w-4" /> {t('security.contactGuard', 'Contact Guard')}
                </Button>
              </div>
            </div>
          )}

          <div className="px-6 py-4 border-t border-[#E0E0E0] bg-[#FAFAFA] flex justify-end gap-2">
            <Button variant="outline" onClick={() => setViewOpen(false)}>{t('common.close')}</Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="bg-[#5B2C93] hover:bg-[#3D1C5E] gap-2">
                  {t('security.takeAction', 'Take Action')} <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{t('security.responseActions', 'Response Actions')}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleAction("Dispatch Patrol")}>{t('security.action.dispatchPatrol', 'Dispatch Patrol Unit')}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleAction("Lockdown Zone")}>{t('security.action.lockdownZone', 'Initiate Zone Lockdown')}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleAction("False Alarm")}>{t('security.action.falseAlarm', 'Mark as False Alarm')}</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleAction("Escalate")} className="text-[#DC2626]">{t('security.action.escalate', 'Escalate to Supervisor')}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
